import React from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";

const slideInRow = keyframes`
  from {
    opacity: 0;
    transform: translateX(-16px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const CampaignsTableContainer = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
`;

const TableHeaderRow = styled.tr``;

const TableHeaderCell = styled.th`
  padding: 14px;
  text-align: left;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 12px;
  color: #414141;
  border-bottom: 1px solid #e0e0e0;
`;

const TableBody = styled.tbody``;

const ClickableTableRow = styled.tr`
  cursor: pointer;
  transition: all 0.2s;
  animation: ${slideInRow} 0.3s ease-out;
  animation-fill-mode: both;

  &:hover {
    background: #f0f4ff;
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(71, 109, 250, 0.08);
  }

  ${(props) =>
    props.index !== undefined &&
    `
    animation-delay: ${props.index * 0.04}s;
  `}
`;

const TableCell = styled.td`
  padding: 14px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: top;
`;

const SurveyInfo = styled.div``;

const SurveyTitle = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
  margin-bottom: 4px;
`;

const SurveySubtitle = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
  margin-top: 2px;
`;

const CellText = styled.div`
  font-family: "Poppins";
  font-weight: 400;
  font-size: 12px;
  color: #7e7e7e;
`;

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 16px;
  border-top: 1px solid #e0e0e0;
`;

const PaginationButton = styled.button`
  padding: 8px 16px;
  background: ${(props) => (props.disabled ? "#f8f9fa" : "#476dfa")};
  border: none;
  border-radius: 6px;
  color: ${(props) => (props.disabled ? "#aaa" : "white")};
  font-family: "Poppins";
  font-weight: 500;
  font-size: 13px;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background: #3d5ce6;
  }
`;

const PaginationInfo = styled.div`
  display: inline-flex;
  align-items: center;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #7e7e7e;
  gap: 4px;
`;

const PageJumpInput = styled.input`
  width: 50px;
  padding: 4px 6px;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  font-family: "Poppins";
  font-weight: 400;
  font-size: 13px;
  color: #414141;
  text-align: center;
  outline: none;
  transition: border-color 0.2s;
  display: inline-block;

  &:focus {
    border-color: #476dfa;
  }

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

export default function CampaignsTable({
  campaigns = [],
  totalPages = 1,
  currentPage = 1,
  pageInputValue = "1",
  onPageChange,
  onPageInputChange,
  onPageJump,
  onPageInputBlur,
}) {
  const navigate = useNavigate();

  const rows = Array.isArray(campaigns) ? campaigns : [];

  const formatDate = (isoString) => {
    if (!isoString) return '—';
    const date = new Date(isoString);
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric', month: 'short', day: 'numeric'
    }).format(date);
  };

  return (
    <TableWrapper>
      <CampaignsTableContainer>
        <TableHeader>
          <TableHeaderRow>
            <TableHeaderCell>Name</TableHeaderCell>
            <TableHeaderCell>Phase</TableHeaderCell>
            <TableHeaderCell>Managers</TableHeaderCell>
            <TableHeaderCell>Actions Created</TableHeaderCell>
            <TableHeaderCell>Start Date</TableHeaderCell>
            <TableHeaderCell>End Date</TableHeaderCell>
          </TableHeaderRow>
        </TableHeader>
        <TableBody>
          {rows.length > 0 ? (
            rows.map((c, index) => (
              <ClickableTableRow
                key={c.id}
                index={index}
                onClick={() => navigate(`/leader/audit/action-plan-v3/campaign/${c.id}`)}
                title="Click to view campaign details"
              >
                <TableCell>
                  <SurveyInfo>
                    <SurveyTitle>{c.name}</SurveyTitle>
                    {c.description && <SurveySubtitle>{c.description}</SurveySubtitle>}
                  </SurveyInfo>
                </TableCell>
                <TableCell>
                  <CellText>{c.current_phase || '—'}</CellText>
                </TableCell>
                <TableCell>
                  <CellText>{c?.selected_managers_count ?? 0}</CellText>
                </TableCell>
                <TableCell>
                  <CellText>{c?.actions_created ?? 0}</CellText>
                </TableCell>
                <TableCell>
                  <CellText>{formatDate(c?.start_date)}</CellText>
                </TableCell>
                <TableCell>
                  <CellText>{formatDate(c?.end_date)}</CellText>
                </TableCell>
              </ClickableTableRow>
            ))
          ) : (
            <ClickableTableRow>
              <TableCell colSpan={6} style={{ color: '#7e7e7e', textAlign: 'center', padding: '24px' }}>
                <CellText>No campaigns yet.</CellText>
              </TableCell>
            </ClickableTableRow>
          )}
        </TableBody>
      </CampaignsTableContainer>

      {totalPages > 1 && (
        <PaginationContainer>
          <PaginationButton
            onClick={() => onPageChange && onPageChange(Math.max(currentPage - 1, 1))}
            disabled={currentPage === 1}
          >
            Previous
          </PaginationButton>

          <PaginationInfo>
            Page{" "}
            <PageJumpInput
              type="number"
              min="1"
              max={totalPages}
              value={pageInputValue}
              onChange={(e) => onPageInputChange && onPageInputChange(e.target.value)}
              onKeyDown={onPageJump}
              onBlur={onPageInputBlur}
            />{" "}
            of {totalPages}
          </PaginationInfo>

          <PaginationButton
            onClick={() =>
              onPageChange && onPageChange(Math.min(currentPage + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            Next
          </PaginationButton>
        </PaginationContainer>
      )}
    </TableWrapper>
  );
}
